import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CircuitBoard,
  Code2,
  Eye,
  EyeOff,
  Plus,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import { adminProjectBuilderAPI } from '../services/adminApi';
import {
  decodeProjectFileContents,
  getProjectImportFallbackName,
  getProjectStoredSimulationCode,
} from '../utils/projectBuilderProjectFiles';

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 18px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SectionDescription = styled.p`
  margin: 6px 0 0 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
  max-width: 760px;
`;

const StatusText = styled.p`
  margin: 0;
  color: ${props => props.$tone === 'error' ? '#b91c1c' : '#475569'};
  font-size: 0.88rem;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.span`
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 800;
`;

const Hint = styled.span`
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.45;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 11px 12px;
  font-size: 0.95rem;
  color: #0f172a;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: ${props => props.$code ? '260px' : '110px'};
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 12px;
  font-size: ${props => props.$code ? '0.9rem' : '0.95rem'};
  line-height: ${props => props.$code ? '1.55' : '1.5'};
  color: #0f172a;
  background: #ffffff;
  resize: vertical;
  font-family: ${props => props.$code ? "'Consolas', 'Monaco', monospace" : 'inherit'};

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  background: #f8fafc;
`;

const ToggleCopy = styled.div`
  min-width: 0;
`;

const ToggleTitle = styled.div`
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 800;
`;

const ToggleHint = styled.p`
  margin: 4px 0 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.45;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const ToggleTrack = styled.span`
  width: 52px;
  height: 30px;
  border-radius: 999px;
  background: ${props => props.$checked ? '#2c9fa4' : '#cbd5e1'};
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
`;

const ToggleThumb = styled.span`
  position: absolute;
  top: 3px;
  left: ${props => props.$checked ? '25px' : '3px'};
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.18);
  transition: left 0.2s ease;
`;

const ToggleText = styled.span`
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
`;

const UploadRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const UploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 10px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #2c9fa4;
    color: #0f766e;
    background: #f0fdfa;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const SummaryStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SummaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  background: ${props => props.$tone === 'code' ? '#eef2ff' : '#f8fafc'};
  color: ${props => props.$tone === 'code' ? '#4338ca' : '#334155'};
  border: 1px solid ${props => props.$tone === 'code' ? '#c7d2fe' : '#dbe3ee'};
  font-size: 0.8rem;
  font-weight: 800;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 10px 14px;
  border: 1px solid ${props => props.$danger ? '#fecaca' : props.$primary ? '#2c9fa4' : '#cbd5e1'};
  border-radius: 10px;
  background: ${props => props.$danger ? '#fff1f2' : props.$primary ? '#2c9fa4' : '#ffffff'};
  color: ${props => props.$danger ? '#b91c1c' : props.$primary ? '#ffffff' : '#0f172a'};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? '#ffe4e6' : props.$primary ? '#247f83' : '#f8fafc'};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ExampleCard = styled.button`
  width: 100%;
  border: 1px solid ${props => props.$selected ? '#99f6e4' : '#e5e7eb'};
  border-radius: 14px;
  padding: 14px;
  background: ${props => props.$selected ? '#f0fdfa' : '#ffffff'};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #5eead4;
    background: #f8fafc;
  }
`;

const ExampleTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const ExampleTitle = styled.div`
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 900;
`;

const ExampleMeta = styled.p`
  margin: 6px 0 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.5;
`;

const ExampleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border-radius: 999px;
  background: ${props => props.$active ? '#ecfdf5' : '#fff7ed'};
  color: ${props => props.$active ? '#047857' : '#c2410c'};
  border: 1px solid ${props => props.$active ? '#a7f3d0' : '#fed7aa'};
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const EmptyState = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  padding: 18px;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
  background: #f8fafc;
`;

const normalizeExampleRecord = (record) => {
  const projectPayload = record?.project_payload ?? record?.projectPayload ?? null;
  const simulationCode = record?.simulation_code ?? record?.simulationCode ?? getProjectStoredSimulationCode(projectPayload);
  const componentCount = Number(record?.component_count ?? record?.componentCount ?? projectPayload?.components?.length ?? 0);
  const connectionCount = Number(record?.connection_count ?? record?.connectionCount ?? projectPayload?.connections?.length ?? 0);
  const hasSimulationCode = record?.has_simulation_code ?? record?.hasSimulationCode;
  const isActive = record?.is_active ?? record?.isActive;

  return {
    id: record?.id ?? null,
    title: String(record?.title || ''),
    description: String(record?.description || ''),
    sourceFileName: String(record?.source_file_name ?? record?.sourceFileName ?? ''),
    componentCount: Number.isFinite(componentCount) ? componentCount : 0,
    connectionCount: Number.isFinite(connectionCount) ? connectionCount : 0,
    hasSimulationCode: hasSimulationCode === true || hasSimulationCode === 1 || hasSimulationCode === '1',
    isActive: !(isActive === false || isActive === 0 || isActive === '0'),
    projectPayload,
    simulationCode: String(simulationCode || ''),
    updatedAt: record?.updated_at ?? record?.updatedAt ?? '',
  };
};

const createEmptyFormState = () => ({
  id: null,
  title: '',
  description: '',
  sourceFileName: '',
  projectPayload: null,
  simulationCode: '',
  isActive: true,
});

const readTextFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error(`Unable to read ${file?.name || 'the selected file'}.`));
  reader.readAsText(file);
});

const deriveExampleTitle = (fileName, projectPayload) => {
  const projectName = String(projectPayload?.name || '').trim();

  if (projectName) {
    return projectName;
  }

  return getProjectImportFallbackName(fileName);
};

const ProjectBuilderExamplesManager = () => {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [selectedExampleId, setSelectedExampleId] = useState(null);
  const [form, setForm] = useState(createEmptyFormState());
  const projectFileInputRef = useRef(null);
  const codeFileInputRef = useRef(null);

  const selectedExample = useMemo(
    () => examples.find((example) => String(example.id) === String(selectedExampleId)) || null,
    [examples, selectedExampleId]
  );

  const projectSummary = useMemo(() => ({
    componentCount: Array.isArray(form.projectPayload?.components) ? form.projectPayload.components.length : 0,
    connectionCount: Array.isArray(form.projectPayload?.connections) ? form.projectPayload.connections.length : 0,
  }), [form.projectPayload]);

  useEffect(() => {
    let isMounted = true;

    const loadExamples = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await adminProjectBuilderAPI.getExamples();
        if (!isMounted) {
          return;
        }

        const nextExamples = Array.isArray(response?.data)
          ? response.data.map(normalizeExampleRecord)
          : [];

        setExamples(nextExamples);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(
          loadError?.response?.data?.error ||
          loadError?.response?.data?.message ||
          loadError?.message ||
          'Failed to load Builder examples.'
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadExamples();

    return () => {
      isMounted = false;
    };
  }, []);

  const resetForm = () => {
    setSelectedExampleId(null);
    setForm(createEmptyFormState());
    setStatus('');
    setError('');
  };

  const loadExampleIntoForm = (example) => {
    if (!example) {
      return;
    }

    setSelectedExampleId(example.id);
    setForm({
      id: example.id,
      title: example.title,
      description: example.description,
      sourceFileName: example.sourceFileName,
      projectPayload: example.projectPayload,
      simulationCode: example.simulationCode,
      isActive: example.isActive,
    });
    setStatus('');
    setError('');
  };

  const handleProjectFileUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      setError('');
      setStatus(`Reading ${file.name}...`);

      const rawContents = await readTextFile(file);
      const projectPayload = decodeProjectFileContents(rawContents);

      if (!projectPayload || !Array.isArray(projectPayload.components) || !Array.isArray(projectPayload.connections)) {
        throw new Error('That file is not a valid Builder project.');
      }

      const nextTitle = deriveExampleTitle(file.name, projectPayload);
      const nextSimulationCode = getProjectStoredSimulationCode(projectPayload);

      setForm((currentForm) => ({
        ...currentForm,
        title: currentForm.title.trim() ? currentForm.title : nextTitle,
        sourceFileName: file.name,
        projectPayload,
        simulationCode: currentForm.simulationCode.trim() ? currentForm.simulationCode : nextSimulationCode,
      }));

      setStatus(`Loaded ${file.name}. Review the title and code, then save.`);
    } catch (uploadError) {
      setStatus('');
      setError(uploadError?.message || 'Failed to read the selected project file.');
    }
  };

  const handleCodeFileUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      setError('');
      const rawContents = await readTextFile(file);

      setForm((currentForm) => ({
        ...currentForm,
        simulationCode: rawContents.replace(/\r\n?/g, '\n'),
      }));
      setStatus(`Loaded code from ${file.name}.`);
    } catch (uploadError) {
      setStatus('');
      setError(uploadError?.message || 'Failed to read the selected code file.');
    }
  };

  const handleSave = async () => {
    if (saving) {
      return;
    }

    if (!form.projectPayload) {
      setError('Upload a Builder .mpbproj file before saving an example.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setStatus(form.id ? 'Saving changes...' : 'Creating example...');

      const payload = {
        title: form.title,
        description: form.description,
        source_file_name: form.sourceFileName,
        project_payload: form.projectPayload,
        simulation_code: form.simulationCode,
        is_active: form.isActive,
      };

      const response = form.id
        ? await adminProjectBuilderAPI.updateExample(form.id, payload)
        : await adminProjectBuilderAPI.createExample(payload);

      const savedExample = normalizeExampleRecord(response?.data?.data || response?.data);

      setExamples((currentExamples) => {
        const nextExamples = [
          savedExample,
          ...currentExamples.filter((example) => String(example.id) !== String(savedExample.id)),
        ];

        return nextExamples.sort((left, right) => {
          const rightDate = String(right.updatedAt || '');
          const leftDate = String(left.updatedAt || '');
          return rightDate.localeCompare(leftDate);
        });
      });

      loadExampleIntoForm(savedExample);
      setStatus(form.id ? 'Example updated.' : 'Example created.');
    } catch (saveError) {
      setStatus('');
      setError(
        saveError?.response?.data?.error ||
        saveError?.response?.data?.message ||
        saveError?.message ||
        'Failed to save the Builder example.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!form.id || saving) {
      return;
    }

    const shouldDelete = window.confirm(`Delete "${form.title || 'this example'}"?`);

    if (!shouldDelete) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setStatus('Deleting example...');

      await adminProjectBuilderAPI.deleteExample(form.id);

      setExamples((currentExamples) => currentExamples.filter((example) => String(example.id) !== String(form.id)));
      resetForm();
      setStatus('Example deleted.');
    } catch (deleteError) {
      setStatus('');
      setError(
        deleteError?.response?.data?.error ||
        deleteError?.response?.data?.message ||
        deleteError?.message ||
        'Failed to delete the Builder example.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Card>
        <SectionHeader>
          <div>
            <SectionTitle>
              <CircuitBoard size={18} />
              Example Editor
            </SectionTitle>
            <SectionDescription>
              Upload a ready-made Builder circuit, review or replace the Arduino code, and choose whether the example is visible in the public builder. Turn on Arduino simulation above if you want visitors to run the uploaded sketch.
            </SectionDescription>
          </div>

          <ActionButton
            type="button"
            onClick={resetForm}
            disabled={saving}
          >
            <Plus size={16} />
            New Example
          </ActionButton>
        </SectionHeader>

        <Grid>
          <Field>
            <FieldLabel>Example title</FieldLabel>
            <Input
              type="text"
              value={form.title}
              onChange={(event) => setForm((currentForm) => ({
                ...currentForm,
                title: event.target.value,
              }))}
              placeholder="Blink an LED with Arduino Uno"
            />
          </Field>

          <Field>
            <FieldLabel>Project file</FieldLabel>
            <Input
              type="text"
              value={form.sourceFileName}
              readOnly
              placeholder="No .mpbproj file selected yet"
            />
          </Field>
        </Grid>

        <Field>
          <FieldLabel>Description</FieldLabel>
          <Textarea
            value={form.description}
            onChange={(event) => setForm((currentForm) => ({
              ...currentForm,
              description: event.target.value,
            }))}
            placeholder="Short note shown in the Open Examples modal."
          />
        </Field>

        <Field>
          <FieldLabel>Uploads</FieldLabel>
          <Hint>
            The project upload fills in the circuit payload. The code upload is optional and is helpful if you want to replace the sketch bundled inside the project file.
          </Hint>

          <UploadRow>
            <UploadButton
              type="button"
              onClick={() => projectFileInputRef.current?.click()}
              disabled={saving}
            >
              <Upload size={16} />
              Upload .mpbproj
            </UploadButton>

            <UploadButton
              type="button"
              onClick={() => codeFileInputRef.current?.click()}
              disabled={saving}
            >
              <Code2 size={16} />
              Upload Code File
            </UploadButton>

            <HiddenFileInput
              ref={projectFileInputRef}
              type="file"
              accept=".mpbproj,.mbproj,.json,application/json,text/plain"
              onChange={handleProjectFileUpload}
            />

            <HiddenFileInput
              ref={codeFileInputRef}
              type="file"
              accept=".ino,.txt,.cpp,.c,.h,.hpp,text/plain"
              onChange={handleCodeFileUpload}
            />
          </UploadRow>
        </Field>

        <SummaryStrip>
          <SummaryBadge>
            <CircuitBoard size={14} />
            {projectSummary.componentCount} component{projectSummary.componentCount === 1 ? '' : 's'}
          </SummaryBadge>
          <SummaryBadge>
            <CircuitBoard size={14} />
            {projectSummary.connectionCount} connection{projectSummary.connectionCount === 1 ? '' : 's'}
          </SummaryBadge>
          <SummaryBadge $tone="code">
            <Code2 size={14} />
            {form.simulationCode.trim() ? 'Code attached' : 'No code attached'}
          </SummaryBadge>
        </SummaryStrip>

        <Field>
          <FieldLabel>Arduino code</FieldLabel>
          <Hint>
            Paste code directly or upload a sketch file. If you leave this blank, the example will load only the circuit.
          </Hint>
          <Textarea
            $code
            value={form.simulationCode}
            onChange={(event) => setForm((currentForm) => ({
              ...currentForm,
              simulationCode: event.target.value,
            }))}
            placeholder="void setup() {\n}\n\nvoid loop() {\n}"
          />
        </Field>

        <ToggleRow>
          <ToggleCopy>
            <ToggleTitle>Visible in public Builder</ToggleTitle>
            <ToggleHint>
              Hidden examples stay in admin so you can finish them before showing them in the public Open Examples modal.
            </ToggleHint>
          </ToggleCopy>

          <ToggleLabel>
            <ToggleInput
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm((currentForm) => ({
                ...currentForm,
                isActive: event.target.checked,
              }))}
            />
            <ToggleTrack $checked={form.isActive}>
              <ToggleThumb $checked={form.isActive} />
            </ToggleTrack>
            <ToggleText>{form.isActive ? 'Visible' : 'Hidden'}</ToggleText>
          </ToggleLabel>
        </ToggleRow>

        <ActionRow>
          <ActionButton
            type="button"
            onClick={handleSave}
            $primary
            disabled={saving || !form.projectPayload}
          >
            <Save size={16} />
            {saving ? 'Saving...' : form.id ? 'Save Changes' : 'Create Example'}
          </ActionButton>

          {form.id ? (
            <ActionButton
              type="button"
              onClick={handleDelete}
              $danger
              disabled={saving}
            >
              <Trash2 size={16} />
              Delete
            </ActionButton>
          ) : null}
        </ActionRow>

        {error ? <StatusText $tone="error">{error}</StatusText> : null}
        {!error && status ? <StatusText>{status}</StatusText> : null}
      </Card>

      <Card>
        <SectionHeader>
          <div>
            <SectionTitle>
              <Code2 size={18} />
              Saved Examples
            </SectionTitle>
            <SectionDescription>
              Pick an example to edit it, or start a fresh one from the editor on the left.
            </SectionDescription>
          </div>
        </SectionHeader>

        {loading ? (
          <StatusText>Loading examples...</StatusText>
        ) : examples.length === 0 ? (
          <EmptyState>
            No Builder examples have been added yet. Upload your first `.mpbproj` file and save it here.
          </EmptyState>
        ) : (
          <List>
            {examples.map((example) => (
              <ExampleCard
                key={example.id}
                type="button"
                $selected={selectedExample?.id === example.id}
                onClick={() => loadExampleIntoForm(example)}
              >
                <ExampleTitleRow>
                  <ExampleTitle>{example.title}</ExampleTitle>
                  <ExampleBadge $active={example.isActive}>
                    {example.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                    {example.isActive ? 'Live' : 'Hidden'}
                  </ExampleBadge>
                </ExampleTitleRow>

                <ExampleMeta>
                  {example.description || 'No description yet.'}
                </ExampleMeta>

                <SummaryStrip>
                  <SummaryBadge>
                    <CircuitBoard size={12} />
                    {example.componentCount} parts
                  </SummaryBadge>
                  <SummaryBadge>
                    <CircuitBoard size={12} />
                    {example.connectionCount} wires
                  </SummaryBadge>
                  <SummaryBadge $tone="code">
                    <Code2 size={12} />
                    {example.hasSimulationCode ? 'Code ready' : 'Circuit only'}
                  </SummaryBadge>
                </SummaryStrip>
              </ExampleCard>
            ))}
          </List>
        )}
      </Card>
    </Layout>
  );
};

export default ProjectBuilderExamplesManager;
